import { Log } from '../AuxillaryService/Log';
import { Signage } from '../Signage';
import { glom } from '../Signage/glom';
import { getPlayerUuid } from '../Signage/utility';
import { envVar } from '../Utility';
import { Handlebars } from '../Utility/Handlebars/databaseHandlebars';
import { $ } from '../../vendor/jquery';
import { moment } from '../../vendor/moment';
import { playCurrentDisplay, stopCurrentDisplay } from '../Display';
import { EMERGENCY_ALERT_CSS_CLASS, numOfActiveAlerts } from './EmergencyAlertsUtility';
import { pushReceiver } from '../MessagingClient/instance';
import { isNullish } from '../Utility/Nullish';

const INTERVAL_MS = process.env.EMERGENCY_ALERTS_INTERVAL_MS
    ? parseInt(process.env.EMERGENCY_ALERTS_INTERVAL_MS)
    : 30000;

let emergencyAlertMechanism: EmergencyAlertService | null = null;

export enum AlertTemplateType {
    background = 'background',
    html = 'html',
}

interface MessageConfig {
    headerHeight?: string;
    footerHeight?: string;
    top?: string;
    height?: string;
    width?: string;
    left?: string;
    sidebarWidth?: string;
    sidebarLocation?: string;
    textOverflow?: string;
    verticalHeaderHeight?: string;
    verticalFooterHeight?: string;
    fontSize?: string;
    minFontSize?: string;
}

interface FontConfig {
    fontSize?: string;
    minFontSize?: string;
}

interface AlertConfiguration {
    template: {
        template_hbs: string;
        type: AlertTemplateType;
        date_format?: string;
        message_config?: string;
        header_config?: string;
        sidebar_config?: string;
        footer_config?: string;
        base64_img_horizontal?: string;
        base64_img_vertical?: string;
        background_size?: string;
    };
    alert_layout_config?: string;
}

interface AlertData {
    identifier: string;
    effectiveDate: string;
    sent: string;
    message_config?: MessageConfig;
    header_config?: FontConfig;
    sidebar_config?: FontConfig;
    footer_config?: FontConfig;
    base64Img?: string;
    background_size?: string;
    nowDate?: string;
}

interface AlertLayoutConfig {
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
}

function extractFontSize(value: string | undefined | null, defaultValue: number): number {
    const fontSize = value;
    if (isNullish(fontSize) || isNaN(Number(fontSize))) {
        return defaultValue;
    }
    return parseInt(fontSize);
}

/**
 * Service class for handling emergency alerts
 */
export class EmergencyAlertService {
    private m_AlertConfig: AlertConfiguration | null = null;
    private m_AlertServiceTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private m_VisibleAlert: AlertData | null = null;
    private m_HbsRenderer: HandlebarsTemplateDelegate | null = null;

    /**
     * Begins monitoring for emergency alerts
     */
    private updateConfig(newConfig: AlertConfiguration, alertConfigId: number): void {
        this.m_AlertConfig = newConfig;
        this.m_HbsRenderer = Handlebars.compile(this.m_AlertConfig.template.template_hbs);
        this.pollEASource(alertConfigId);
    }

    /**
     * Begins monitoring for emergency alerts
     */
    public async beginEmergencyAlertMonitor(): Promise<void> {
        const playerUuid = getPlayerUuid();
        // Get configuration for this player
        if (glom.alertConfigId && playerUuid) {
            const alertConfigId = glom.alertConfigId;
            // Add push receiver listener for emergency alert feed updates
            pushReceiver().addListener('emergency-alert-feed-update', async () => {
                this.pollEASource(alertConfigId);
            });

            const config = await this.acquireEAConfiguration(alertConfigId, playerUuid);
            if (config.configuration) {
                this.updateConfig(config.configuration, alertConfigId);
            }
        }
    }

    /**
     * Acquires emergency alert configuration from the API
     */
    private acquireEAConfiguration(
        configId: number,
        playerUuid: string
    ): Promise<{ configuration: AlertConfiguration }> {
        return new Promise((resolve, reject) => {
            $.ajax({
                dataType: 'json',
                cache: false,
                url:
                    envVar(process.env.BASE_URL_EMERGENCY_ALERTS) +
                    '/configuration/by_id/' +
                    configId,
                type: 'GET',
                data: {
                    player_uuid: playerUuid,
                },
                timeout: 10000,
                success: resolve,
                error: reject,
            });
        });
    }

    /**
     * Polls the emergency alert source for new alerts
     */
    private async pollEASource(configId: number): Promise<void> {
        if (this.m_AlertServiceTimeoutId) {
            clearTimeout(this.m_AlertServiceTimeoutId);
            this.m_AlertServiceTimeoutId = null;
        }

        try {
            const data = await $.ajax({
                dataType: 'json',
                cache: false,
                url: envVar(process.env.BASE_URL_EMERGENCY_ALERTS) + '/feed',
                type: 'GET',
                data: {
                    config_id: configId,
                },
                timeout: 10000,
            });

            this.m_AlertServiceTimeoutId = setTimeout(
                () => this.pollEASource(configId),
                INTERVAL_MS
            );

            if (data && data.alerts && data.alerts.length > 0) {
                // Looks like we have an alert!
                this.showAlert(configId, data.alerts[0]);
            } else {
                this.clearAlert(configId);
            }
        } catch (error) {
            this.m_AlertServiceTimeoutId = setTimeout(
                () => this.pollEASource(configId),
                INTERVAL_MS
            );
        }
    }

    /**
     * Shows an emergency alert
     */
    private showAlert(configId: number, alertObj: AlertData): void {
        if (
            this.m_VisibleAlert &&
            this.m_VisibleAlert.identifier &&
            this.m_VisibleAlert.identifier !== alertObj.identifier
        ) {
            this.clearAlert(configId);
        }

        if (!this.m_VisibleAlert) {
            alertObj.nowDate = '<span class="alert-now-date">#now#</span>';

            let alertLayoutConfig: AlertLayoutConfig[] | null = null;

            if (this.m_AlertConfig && this.m_AlertConfig.alert_layout_config) {
                alertLayoutConfig = JSON.parse(this.m_AlertConfig.alert_layout_config);
            }

            if (Signage.localTimeZone && alertObj.effectiveDate) {
                try {
                    const dateFormat: string = this.m_AlertConfig?.template?.date_format ?? 'LLL';

                    const publishMomentDate = moment.tz(alertObj.effectiveDate, 'UTC');
                    const sentMomentDate = moment.tz(alertObj.sent, 'UTC');

                    const publishDateString = publishMomentDate
                        .tz(Signage.localTimeZone)
                        .format(dateFormat);
                    const sentDateString = sentMomentDate
                        .tz(Signage.localTimeZone)
                        .format(dateFormat);

                    alertObj.effectiveDate =
                        publishDateString !== 'Invalid date'
                            ? publishDateString
                            : alertObj.effectiveDate;

                    alertObj.sent =
                        sentDateString !== 'Invalid date' ? sentDateString : alertObj.sent;
                } catch (e) {
                    Log.Info(String(e), null, __fileline, e);
                }
            }

            if (this.m_AlertConfig?.template?.type && this.m_AlertConfig.template.type === 'html') {
                // Parse configuration settings for html based alert template
                if (this.m_AlertConfig.template.message_config) {
                    alertObj.message_config = JSON.parse(
                        this.m_AlertConfig.template.message_config
                    );
                }
                if (this.m_AlertConfig.template.header_config) {
                    alertObj.header_config = JSON.parse(this.m_AlertConfig.template.header_config);
                }
                if (this.m_AlertConfig.template.sidebar_config) {
                    alertObj.sidebar_config = JSON.parse(
                        this.m_AlertConfig.template.sidebar_config
                    );
                }
                if (this.m_AlertConfig.template.footer_config) {
                    alertObj.footer_config = JSON.parse(this.m_AlertConfig.template.footer_config);
                }
            } else if (
                this.m_AlertConfig?.template?.type &&
                this.m_AlertConfig.template.type === 'background' &&
                this.m_AlertConfig.template.message_config
            ) {
                alertObj.message_config = JSON.parse(this.m_AlertConfig.template.message_config);

                if (alertObj.message_config) {
                    // Determine height and position of alert message area
                    if (
                        alertObj.message_config.headerHeight &&
                        parseInt(alertObj.message_config.headerHeight || '0') > 0
                    ) {
                        alertObj.message_config.height =
                            100 -
                            parseInt(alertObj.message_config.headerHeight || '0') -
                            parseInt(alertObj.message_config.footerHeight || '0') +
                            '%';
                        alertObj.message_config.top =
                            parseInt(alertObj.message_config.headerHeight || '0') + '%';
                    } else {
                        alertObj.message_config.top = '0%';
                        alertObj.message_config.height = '100%';
                    }

                    // Determine horizontal position and location of alert message
                    // area
                    if (
                        alertObj.message_config.sidebarWidth &&
                        parseInt(alertObj.message_config.sidebarWidth || '0') > 0
                    ) {
                        alertObj.message_config.width =
                            100 - parseInt(alertObj.message_config.sidebarWidth || '0') + '%';
                        alertObj.message_config.left =
                            alertObj.message_config.sidebarLocation === 'left'
                                ? alertObj.message_config.sidebarWidth
                                : '0%';
                    } else {
                        alertObj.message_config.width = '100%';
                    }

                    // Determine which background image to use based on screen
                    // orientation
                    if (
                        Signage.width < Signage.height &&
                        this.m_AlertConfig.template.base64_img_vertical
                    ) {
                        alertObj.base64Img = this.m_AlertConfig.template.base64_img_vertical;
                        if (alertObj.message_config.verticalHeaderHeight) {
                            alertObj.message_config.height =
                                100 -
                                parseInt(alertObj.message_config.verticalHeaderHeight || '0') -
                                parseInt(alertObj.message_config.verticalFooterHeight || '0') +
                                '%';
                            alertObj.message_config.top =
                                parseInt(alertObj.message_config.verticalHeaderHeight || '0') + '%';
                        }
                    }
                }

                alertObj.base64Img = this.m_AlertConfig.template.base64_img_horizontal;

                alertObj.background_size = this.m_AlertConfig.template.background_size;
            }

            this.m_VisibleAlert = alertObj;

            if (alertLayoutConfig) {
                // Fixed alert pages
                alertLayoutConfig.forEach((element) => {
                    if (this.m_HbsRenderer) {
                        $('body').append(
                            `<div id="alert-container-${configId}-${element.page}" class="${EMERGENCY_ALERT_CSS_CLASS}" style="z-index:999999; left:${element.x};top:${element.y};width:${element.width};height:${element.height};position:absolute;">` +
                                this.m_HbsRenderer(alertObj) +
                                '</div>'
                        );
                    }
                });
            } else {
                // Full screen alert
                if (this.m_HbsRenderer) {
                    $('body').append(
                        `<div id="alert-container-${configId}" class="${EMERGENCY_ALERT_CSS_CLASS}" style="z-index:999999; width:100%;height:100%;position:absolute;">` +
                            this.m_HbsRenderer(alertObj) +
                            '</div>'
                    );
                }
            }

            const textFillAlert = () => {
                // Textfill
                if (this.m_AlertConfig?.template.type === 'html') {
                    // Set max font for text resizing to fit containers.
                    const maxHeaderFont = extractFontSize(alertObj.header_config?.fontSize, 65);
                    const maxMessageFont = extractFontSize(alertObj.message_config?.fontSize, 55);
                    const maxSidebarFont = extractFontSize(alertObj.sidebar_config?.fontSize, 55);
                    const maxFooterFont = extractFontSize(alertObj.footer_config?.fontSize, 55);
                    // Set min font for text resizing to fit containers.
                    const minHeaderFont = extractFontSize(alertObj.header_config?.minFontSize, 32);
                    const minMessageFont = extractFontSize(
                        alertObj.message_config?.minFontSize,
                        32
                    );
                    const minSidebarFont = extractFontSize(
                        alertObj.sidebar_config?.minFontSize,
                        16
                    );
                    const minFooterFont = extractFontSize(alertObj.footer_config?.minFontSize, 16);

                    $('body')
                        .find('.alert-header')
                        .textfill({
                            minFontPixels: minHeaderFont,
                            maxFontPixels: maxHeaderFont,
                            fail: () => {
                                $('body')
                                    .find('.alert-header-div span')
                                    .css({ 'font-size': minHeaderFont });
                            },
                        });
                    $('body')
                        .find('.alert-message-div')
                        .textfill({
                            minFontPixels: minMessageFont,
                            maxFontPixels: maxMessageFont,
                            changeLineHeight: true,
                            allowOverflow: true,
                            fail: () => {
                                $('body')
                                    .find('.alert-message-div span')
                                    .css({ 'font-size': minMessageFont });
                            },
                        });
                    $('body')
                        .find('.alert-sidebar')
                        .textfill({
                            minFontPixels: minSidebarFont,
                            maxFontPixels: maxSidebarFont,
                            fail: () => {
                                $('body')
                                    .find('.alert-sidebar-div span')
                                    .css({ 'font-size': minSidebarFont });
                            },
                        });
                    $('body')
                        .find('.alert-footer')
                        .textfill({
                            minFontPixels: minFooterFont,
                            maxFontPixels: maxFooterFont,
                            fail: () => {
                                $('body')
                                    .find('.alert-footer-div span')
                                    .css({ 'font-size': minFooterFont });
                            },
                        });
                } else if (
                    this.m_AlertConfig?.template.type === 'background' &&
                    alertObj.message_config
                ) {
                    if (alertObj.message_config.textOverflow === 'dynamic') {
                        $('body').find('.alert-text').textfill();
                    }
                }
            };

            if (this.m_AlertConfig?.template?.type) {
                textFillAlert();
            }

            const bringAlertToFront = () => {
                if (this.m_VisibleAlert) {
                    try {
                        stopCurrentDisplay();
                    } catch (dontneederrormessage) {
                        // If it fails to bring the alert to the front at first,
                        // we still want to continue to make sure that it can be
                        // attempted to be brought to the front in 5 seconds
                        // from now
                    }

                    // HACK!!! Keep trying incase the signage is still loading
                    setTimeout(bringAlertToFront, 5000);
                }
            };

            if (this.m_AlertConfig?.template?.type) {
                setTimeout(textFillAlert, 10000);
            }

            setTimeout(bringAlertToFront, 5000);
        }
    }

    /**
     * Clears the current emergency alert
     */
    private clearAlert(configId: number): void {
        if (this.m_VisibleAlert) {
            if (this.m_AlertConfig && this.m_AlertConfig.alert_layout_config) {
                $(`.${EMERGENCY_ALERT_CSS_CLASS}`).remove();
            }
            $(`#alert-container-${configId}`).remove();

            if (numOfActiveAlerts() === 0) {
                playCurrentDisplay();
            }
        }

        this.m_VisibleAlert = null;
    }
}

/**
 * Initializes emergency alerts
 */
export function initializeEmergencyAlerts(): void {
    if (emergencyAlertMechanism === null) {
        emergencyAlertMechanism = new EmergencyAlertService();
    }
    emergencyAlertMechanism.beginEmergencyAlertMonitor();
}

/**
 * Gets the emergency alert service instance
 */
export function getEmergencyAlertService(): EmergencyAlertService | null {
    return emergencyAlertMechanism;
}
